import type { ConversionFactorUnit } from './unit.model';
import type { IfcMeasure } from '../helpers/ifc-quantity.helpers';

/** Why a resource cannot be mapped to a generic material product. */
export const AI_IMPORT_MAPPING_UNSUPPORTED_REASONS = [
  'transport',
  'price_addon',
  'construction',
  'insufficient_evidence',
  'other',
] as const;

export type AiImportMappingUnsupportedReason =
  (typeof AI_IMPORT_MAPPING_UNSUPPORTED_REASONS)[number];

/** Cited source for a product match or conversion factor. */
export type AiImportMappingSource = {
  title: string;
  /** Web URL when the source is online; null for catalogue-only evidence. */
  url: string | null;
};

export type AiImportMappingFactor = {
  /** `value_to = qty_from * quantityFactor`. */
  quantityFactor: number;
  unitTo: ConversionFactorUnit;
  confidence: number;
  sources: AiImportMappingSource[];
};

export type AiImportMappingProductSuggestion = {
  kind: 'product';
  productId: string;
  productConfidence: number;
  factor: AiImportMappingFactor | null;
  rationale: string;
  sources: AiImportMappingSource[];
};

export type AiImportMappingUnsupportedSuggestion = {
  kind: 'unsupported';
  reason: AiImportMappingUnsupportedReason;
  rationale: string;
};

export type AiImportMappingSuggestion =
  AiImportMappingProductSuggestion | AiImportMappingUnsupportedSuggestion;

/** Validated AI mapping response for one import row. */
export type AiImportMappingResponse = {
  rowId: string;
  fingerprint: string;
  suggestions: AiImportMappingSuggestion[];
};

/** Compact catalogue row sent to the model (Boverket / CO2data.fi only). */
export type AiImportMappingCatalogItem = {
  id: string;
  name: string;
  source: string;
  unit: ConversionFactorUnit;
  conversion_factors: Partial<Record<ConversionFactorUnit, number>>;
};

/** Resource fields the model uses to identify a product and factor. */
export type AiImportMappingRowPayload = {
  rowId: string;
  fingerprint: string;
  name: string | null;
  resourceCode: string | null;
  quantity: number | undefined;
  unit: string | undefined;
  sbef: string | undefined;
  extraFields: Record<string, string>;
};

/** Body for `POST /element-imports/:id/ai-mapping-suggestions`. */
export type AiImportMappingRequest = {
  rowId: string;
  /** Active IFC quantity column when the user has pinned one (DEV-3603). */
  ifcMeasure?: IfcMeasure;
};
